// Meta review uses server-generated, signed provider iframes. CTV/DSP retain
// browser-local rendering because their provider preview APIs are separate.

import { Fragment, useEffect, useMemo, useState } from 'react';
import type { AssetAnimation } from '@/api/types.gen';
import { Icons } from '@/components/primitives';
import { RequiredField, useRequiredFeedback, type RequiredIssue } from '@/components/required-field';
import { MetaPreviewGallery } from '@/components/meta-preview-gallery';
import { DisplayAnimationNote } from '@/components/display-animation-note';
import {
  ExternalApprovalRecord,
  ShareAgencyModal,
} from '@/components/creative-review-share';
import type { CreativeChannel } from './creative-config';

const metaPreviewFieldKey = (creativeId: string, placementKey: string) =>
  `preview-meta-${creativeId}-${placementKey}`;
const metaPreviewCollectionFieldKey = 'preview-meta-creative-collection';
const platformPreviewFieldKey = (kind: ReviewKind, itemId: string, placementId?: string) =>
  `preview-${kind}-${itemId}-${placementId || 'missing-assets'}`;

/** The two channels reviewed in the browser: CTV spots and Display banner groups. */
export type ReviewKind = 'ctv' | 'dsp';

/** One placement's review decision. `pending` is also what a missing entry means. */
export type ReviewDecision = 'approved' | 'rejected' | 'pending';

/** Decisions keyed `${itemId}::${placementId || 'main'}`. */
export type ReviewDecisions = Record<string, ReviewDecision>;

/** What review reads of any asset: a local upload carries its `File`, a library
 *  preview carries none. */
export interface ReviewMedia {
  url?: string | null;
  file?: File | null;
  name?: string | null;
}

/** A CTV spot. `duration` is in seconds. */
export interface ReviewVideo extends ReviewMedia {
  duration?: number | null;
}

/** A Display banner at its own size. */
export interface ReviewBanner extends ReviewMedia {
  id?: string;
  w: number;
  h: number;
  size?: { name: string } | null;
  animation?: Pick<AssetAnimation, 'frame_count' | 'cycle_ms' | 'plays'> | null;
  /** A library banner's stored size; a local one reads `file.size`. */
  byteSize?: number | null;
}

/** One CTV creative's or Display group's builder data, as review reads it. */
export interface ReviewItemData {
  destUrl?: string | null;
  asset?: ReviewVideo | null;
  banners?: readonly ReviewBanner[];
}

/** A CTV creative or a Display banner group. */
export interface ReviewItem {
  id: string;
  label: string;
}

/** The part of an item's data that `seededApprovals` reads. */
export interface SeedItemData {
  asset?: ReviewMedia | null;
  banners?: readonly (ReviewMedia & { id?: string })[];
}

// Rebuild the approvals map for placements whose creative already passed
// internal review — the wizard records that review durably as the creative's
// `approved` status at save time, and this is the read-back half. Without it
// a page reload demanded the whole review again (and the step's green state
// silently reverted).
function seededApprovals(
  kind: ReviewKind,
  items: readonly Pick<ReviewItem, 'id'>[],
  data: Readonly<Record<string, SeedItemData | undefined>>,
  approvedItemIds?: ReadonlySet<string>,
): ReviewDecisions {
  const seeded: ReviewDecisions = {};
  if (!approvedItemIds?.size) return seeded;
  // A locally uploaded asset carries a `File`; a library asset does not. The
  // recorded approval belongs to the library asset alone, so replacing a
  // video or adding a banner leaves that placement unreviewed — seeding it
  // would sign off on something nobody has looked at.
  const fromLibrary = (asset: ReviewMedia | null | undefined) => !!asset?.url && !asset.file;
  for (const item of items) {
    if (!approvedItemIds.has(item.id)) continue;
    if (kind === 'ctv') {
      if (fromLibrary(data[item.id]?.asset)) seeded[`${item.id}::video`] = 'approved';
      continue;
    }
    for (const banner of (data[item.id]?.banners || []).filter(fromLibrary)) {
      seeded[`${item.id}::${banner.id || 'main'}`] = 'approved';
    }
  }
  return seeded;
}

export interface PreviewApproveProps {
  channel: CreativeChannel;
  production?: { title?: string | null } | null;
  /** The saved Meta creatives whose official previews are reviewed. */
  previewCreativeIds?: string[];
  confirmed: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  ctvCreatives?: readonly ReviewItem[] | null;
  ctvData?: Readonly<Record<string, ReviewItemData | undefined>> | null;
  dspGroups?: readonly ReviewItem[] | null;
  dspData?: Readonly<Record<string, ReviewItemData | undefined>> | null;
  /** Slots whose library creative already passed review (CTV and Display only). */
  approvedItemIds?: ReadonlySet<string>;
  locked?: boolean;
}

function PreviewApprove({ channel, production, previewCreativeIds = [], confirmed, onConfirm, onEdit, ctvCreatives, ctvData, dspGroups, dspData, approvedItemIds = undefined, locked = false }: PreviewApproveProps) {
  const [shareOpen, setShareOpen] = useState(false);
  const [reviewed, setReviewed] = useState<Record<string, boolean>>({});
  const requiredFeedback = useRequiredFeedback();
  const requiredIssues: RequiredIssue[] = channel !== 'meta'
    ? []
    : previewCreativeIds.length === 0
      ? [{
          key: metaPreviewCollectionFieldKey,
          message: 'Save and submit at least one creative to generate official Meta previews.',
        }]
      : previewCreativeIds.flatMap((creativeId) => reviewed[creativeId]
        ? []
        : [{
            key: metaPreviewFieldKey(creativeId, 'official'),
            message: 'Review every official Meta placement preview before continuing.',
          }]);
  // One stable callback per creative. An inline arrow here would change identity
  // on every render, and the updater must bail out when the value is unchanged —
  // otherwise the gallery's completion effect and this state form a render loop.
  const previewIdSignature = previewCreativeIds.join('|');
  const handleReviewChange = useMemo(
    () => Object.fromEntries(
      (previewIdSignature ? previewIdSignature.split('|') : []).map((creativeId) => [
        creativeId,
        (complete: boolean) => setReviewed((current) => (
          current[creativeId] === complete
            ? current
            : { ...current, [creativeId]: complete }
        )),
      ]),
    ),
    [previewIdSignature],
  );
  const allApproved = channel === 'meta'
    && previewCreativeIds.length > 0
    && requiredIssues.length === 0;
  const unresolvedKeySignature = requiredIssues.map((issue) => issue.key).join('|');

  useEffect(() => {
    const unresolved = new Set(requiredIssues.map((issue) => issue.key));
    Object.keys(requiredFeedback.messages).forEach((key) => {
      if (!unresolved.has(key)) requiredFeedback.clear(key);
    });
  }, [unresolvedKeySignature, requiredFeedback.messages, requiredFeedback.clear]);

  const confirmPreviews = () => {
    if (!requiredFeedback.report(requiredIssues)) return;
    onConfirm?.();
  };

  if (channel === 'ctv') {
    return <CtvDspApprove kind="ctv" items={ctvCreatives||[]} data={ctvData||{}} approvedItemIds={approvedItemIds} confirmed={confirmed} onConfirm={onConfirm} onEdit={onEdit} locked={locked}/>;
  }
  if (channel === 'dsp') {
    return <CtvDspApprove kind="dsp" items={dspGroups||[]} data={dspData||{}} approvedItemIds={approvedItemIds} confirmed={confirmed} onConfirm={onConfirm} onEdit={onEdit} locked={locked}/>;
  }

  return (
    <div>
      <div className="flex between items-center mb-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5,maxWidth:560}}>
          These previews are rendered and signed by Meta from the exact creative spec. Every enabled placement must be reviewed.
        </div>
        <button className="btn btn-primary" onClick={()=>setShareOpen(true)}>
          {Icons.send} Share with agency
        </button>
      </div>

      <RequiredField
        fieldKey={metaPreviewCollectionFieldKey}
        message={requiredFeedback.messages[metaPreviewCollectionFieldKey]}
        attempt={requiredFeedback.attempt}
        surface
      >
        <div className="preview-stack">
          {previewCreativeIds.map((creativeId) => (
            <Fragment key={creativeId}>
              <MetaPreviewGallery
                creativeId={creativeId}
                autoRefresh
                onReviewChange={handleReviewChange[creativeId]}
              />
              {/* Advisory. External sign-off is recorded and shown here, but the
                  approval gate above is unaffected by it — a signed-in user
                  still makes the Review → Approved transition. */}
              <ExternalApprovalRecord creativeId={creativeId} />
            </Fragment>
          ))}
        </div>
      </RequiredField>

      <div className="flex between items-center mt-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {confirmed
            ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> All previews reviewed. Edit creative to re-render.</>
            : (allApproved
                ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> All placements reviewed across every creative.</>
                : null)}
        </div>
        {confirmed
          ? <button className="btn btn-sm" disabled={locked} onClick={onEdit}>Edit previews</button>
          : <button className="btn btn-primary" onClick={confirmPreviews}>
              {Icons.check}{' '}
              {allApproved ? 'Confirm previews as reviewed' : <>Review all official placements to continue</>}
            </button>}
      </div>

      <ShareAgencyModal open={shareOpen} onClose={()=>setShareOpen(false)} creativeIds={previewCreativeIds} production={production}/>
    </div>
  );
}

export interface CtvDspApproveProps {
  kind: ReviewKind;
  items: readonly ReviewItem[];
  data: Readonly<Record<string, ReviewItemData | undefined>>;
  confirmed: boolean;
  onConfirm?: () => void;
  onEdit?: () => void;
  /** Slots whose library creative already passed review; their library placements start reviewed. */
  approvedItemIds?: ReadonlySet<string>;
  locked?: boolean;
}

/** One reviewable surface of an item: the CTV spot, or one Display banner. */
interface ReviewPlacement {
  id: string | undefined;
  label: string;
  banner: ReviewBanner | null;
}

function CtvDspApprove({ kind, items, data, confirmed, onConfirm, onEdit, approvedItemIds = undefined, locked = false }: CtvDspApproveProps) {
  const [approvals, setApprovals] = useState<ReviewDecisions>(() =>
    seededApprovals(kind, items, data, approvedItemIds));
  const requiredFeedback = useRequiredFeedback();

  const placementKey = (itemId: string, subId: string | undefined) => `${itemId}::${subId || 'main'}`;
  const setAp = (itemId: string, subId: string | undefined, val: 'approved' | 'rejected') => setApprovals(a => {
    const k = placementKey(itemId, subId);
    return { ...a, [k]: a[k] === val ? 'pending' : val };
  });

  // Build placements list per item: CTV = 1 placement (the video), DSP = one per banner
  const itemPlacements = items.map((it): { item: ReviewItem; placements: ReviewPlacement[] } => {
    if (kind === 'ctv') {
      return {
        item: it,
        placements: data[it.id]?.asset?.url
          ? [{ id: 'video', label: 'CTV Spot', banner: null }]
          : [],
      };
    }
    const banners = (data[it.id]?.banners || []).filter((banner) => !!banner.url);
    return { item: it, placements: banners.map(b => ({ id: b.id, label: `${b.w}×${b.h}`, banner: b })) };
  });

  const totalPlacements = itemPlacements.reduce((s, x) => s + x.placements.length, 0);
  const approvedCount = itemPlacements.reduce((s, x) => s + x.placements.filter(p => approvals[placementKey(x.item.id, p.id)] === 'approved').length, 0);
  const rejectedCount = itemPlacements.reduce((s, x) => s + x.placements.filter(p => approvals[placementKey(x.item.id, p.id)] === 'rejected').length, 0);
  const allApproved = itemPlacements.length > 0
    && itemPlacements.every((item) => item.placements.length > 0)
    && approvedCount === totalPlacements;
  const collectionRequiredKey = platformPreviewFieldKey(kind, 'collection', 'items');
  const requiredIssues: RequiredIssue[] = itemPlacements.length === 0
    ? [{
        key: collectionRequiredKey,
        message: `Add at least one ${kind === 'ctv' ? 'CTV creative' : 'display banner group'} before confirming previews.`,
      }]
    : itemPlacements.flatMap(({ item, placements }): RequiredIssue[] => {
    if (placements.length === 0) {
      return [{
        key: platformPreviewFieldKey(kind, item.id),
        message: `Add the required ${kind === 'ctv' ? 'video' : 'banner'} for ${item.label} before continuing.`,
      }];
    }
    return placements.flatMap((placement) => {
      const approval = approvals[placementKey(item.id, placement.id)] || 'pending';
      return approval === 'approved'
        ? []
        : [{
            key: platformPreviewFieldKey(kind, item.id, placement.id),
            message: approval === 'rejected'
              ? `Resolve the requested changes and mark ${placement.label} reviewed for ${item.label}.`
              : `Mark ${placement.label} reviewed for ${item.label} before continuing.`,
          }];
    });
      });
  const unresolvedKeySignature = requiredIssues.map((issue) => issue.key).join('|');

  useEffect(() => {
    const unresolved = new Set(requiredIssues.map((issue) => issue.key));
    Object.keys(requiredFeedback.messages).forEach((key) => {
      if (!unresolved.has(key)) requiredFeedback.clear(key);
    });
  }, [unresolvedKeySignature, requiredFeedback.messages, requiredFeedback.clear]);

  const confirmPreviews = () => {
    if (!requiredFeedback.report(requiredIssues)) return;
    onConfirm?.();
  };

  const approveAllItem = (itemId: string, placements: readonly ReviewPlacement[]) => setApprovals(a => {
    const next = { ...a };
    placements.forEach(p => { next[placementKey(itemId, p.id)] = 'approved'; });
    return next;
  });

  const kindName = kind === 'ctv' ? 'CTV' : 'DISPLAY';
  const itemNoun = kind === 'ctv' ? 'creative' : 'banner group';

  return (
    <div>
      <div className="flex between items-center mb-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5,maxWidth:560}}>
          {kind === 'ctv'
            ? 'Review every CTV creative before the local publishing handoff. No TTD creative is created.'
            : 'Review every banner in every group before the local publishing handoff. No TTD creative is created.'}
        </div>
        {/* No agency-share affordance in this step: a saved Display creative
            is shared from the Creative Library drawer, and CTV creatives cannot
            be shared at all (the review link refuses them). */}
      </div>

      <RequiredField
        fieldKey={collectionRequiredKey}
        message={requiredFeedback.messages[collectionRequiredKey]}
        attempt={requiredFeedback.attempt}
        surface
      >
        <div className="preview-stack">
          {itemPlacements.map(({ item, placements }, idx) => {
          const itemData: ReviewItemData = data[item.id] || {};
          const itemApprovedCount = placements.filter(p => approvals[placementKey(item.id, p.id)] === 'approved').length;
          const itemRejectedCount = placements.filter(p => approvals[placementKey(item.id, p.id)] === 'rejected').length;
          const itemReady = placements.length > 0;
          const itemRequiredKey = platformPreviewFieldKey(kind, item.id);
          return (
            <RequiredField
              key={item.id}
              fieldKey={itemRequiredKey}
              message={requiredFeedback.messages[itemRequiredKey]}
              attempt={requiredFeedback.attempt}
              surface
            >
            <div className="preview-creative">
              <div className="preview-creative-hd">
                <div className="flex items-center gap-3">
                  <div className="preview-creative-num mono">{kind === 'ctv' ? 'CV' : 'BG'}{String(idx+1).padStart(2,'0')}</div>
                  <div>
                    <div className="preview-creative-name">{item.label}</div>
                    <div className="preview-creative-meta mono">{kindName} · {placements.length} {kind === 'ctv' ? 'SPOT' : 'BANNER'}{placements.length===1?'':'S'}{itemData.destUrl ? ' · DEST URL SET' : ' · NO DEST URL'}</div>
                  </div>
                </div>
                <div className="preview-status">
                  {itemReady ? (
                    <span className="ps ps-ready">
                      <span className="dot"/>READY · {itemApprovedCount}/{placements.length} REVIEWED
                      {itemRejectedCount > 0 && <> · {itemRejectedCount} CHANGES</>}
                    </span>
                  ) : (
                    <span className="ps ps-idle"><span className="dot"/>NO ASSETS</span>
                  )}
                  {itemReady && (
                    <button className="btn btn-sm" onClick={()=>approveAllItem(item.id, placements)} style={{marginLeft:8}}>Mark all reviewed</button>
                  )}
                </div>
              </div>

              {placements.length === 0 ? (
                <div className="muted" style={{padding:'20px 4px',fontSize:12}}>No {kind === 'ctv' ? 'video uploaded yet' : 'banners uploaded yet'} for this {itemNoun}. Add assets in section 4 to render a preview.</div>
              ) : (
                <div className="placements-grid">
                  {placements.map(p => {
                    const ap = approvals[placementKey(item.id, p.id)] || 'pending';
                    // A CTV placement carries no banner and a Display one always does.
                    const banner = p.banner;
                    const aspect = kind === 'ctv' || !banner ? '16/9' : `${banner.w}/${banner.h}`;
                    const asset = kind === 'ctv' ? itemData.asset : banner;
                    const videoDuration = itemData.asset?.duration;
                    const requiredKey = platformPreviewFieldKey(kind, item.id, p.id);
                    return (
                      <RequiredField
                        key={p.id}
                        fieldKey={requiredKey}
                        message={requiredFeedback.messages[requiredKey]}
                        attempt={requiredFeedback.attempt}
                        surface
                      >
                      <div className="placement">
                        <div className="placement-hd">
                          <div className="flex items-center gap-2">
                            <span className="placement-surface surface-plain">{kind==='ctv' ? 'TV' : 'AD'}</span>
                            <span className="placement-name">{p.label}</span>
                          </div>
                          <span className="placement-spec mono">{kind === 'ctv' ? (videoDuration ? videoDuration + 's' : '—') : (banner?.size?.name || '')}</span>
                        </div>
                        <div className="placement-frame" style={{aspectRatio: aspect, background: '#000'}}>
                          {asset?.url ? (
                            kind === 'ctv' ? (
                              <video src={asset.url} muted loop autoPlay playsInline style={{width:'100%',height:'100%',objectFit:'contain',display:'block'}}/>
                            ) : (
                              <img src={asset.url} alt={asset.name || ''} style={{width:'100%',height:'100%',objectFit:'contain',display:'block'}}/>
                            )
                          ) : (
                            <div style={{width:'100%',height:'100%',display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontFamily:'var(--font-mono)',fontSize:10,letterSpacing:'.12em',opacity:.5}}>NO ASSET</div>
                          )}
                        </div>
                        {kind === 'dsp' && banner && <DisplayAnimationNote animation={banner.animation} byteSize={banner.file?.size ?? banner.byteSize}/>}
                        <div className="placement-actions">
                          <div className="placement-approval">
                            {ap === 'approved' && <span className="ap-chip ap-good"><span>✓</span> Reviewed</span>}
                            {ap === 'rejected' && <span className="ap-chip ap-bad"><span>!</span> Changes requested</span>}
                            {ap === 'pending' && <span className="ap-chip ap-pending"><span>○</span> Pending review</span>}
                          </div>
                          <div className="placement-buttons">
                            <button className={`ap-btn ${ap==='rejected'?'on-bad':''}`} onClick={()=>setAp(item.id, p.id, 'rejected')}>Request changes</button>
                            <button data-validation-focus className={`ap-btn ap-btn-primary ${ap==='approved'?'on-good':''}`} onClick={()=>setAp(item.id, p.id, 'approved')}>{ap==='approved' ? '✓ Reviewed' : 'Mark reviewed'}</button>
                          </div>
                        </div>
                      </div>
                      </RequiredField>
                    );
                  })}
                </div>
              )}
            </div>
            </RequiredField>
          );
          })}
        </div>
      </RequiredField>

      <div className="flex between items-center mt-3" style={{gap:10}}>
        <div className="muted" style={{fontSize:11.5}}>
          {confirmed
            ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> All {kindName.toLowerCase()} previews reviewed. Edit creative to re-render.</>
            : (allApproved
                ? <><span style={{color:'var(--good)'}}>{Icons.check}</span> All {kind === 'ctv' ? 'creatives' : 'banners'} reviewed across every {itemNoun}.</>
                : null)}
        </div>
        {confirmed
          ? <button className="btn btn-sm" disabled={locked} onClick={onEdit}>Edit previews</button>
          : <button className="btn btn-primary" onClick={confirmPreviews}>
              {Icons.check}{' '}
              {allApproved
                ? 'Confirm previews as reviewed'
                : <>Mark every placement reviewed to continue</>}
            </button>}
      </div>

    </div>
  );
}

export { PreviewApprove, ShareAgencyModal, CtvDspApprove, seededApprovals };
